import { faExclamationCircle, faCircleNotch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";
import { Accordion, Badge, Button, Card, Dropdown, Form, FormControl, Table } from "react-bootstrap";
import { withTranslation } from "react-i18next";
import * as XLSX from "xlsx";
import BaseDialog from "../../../components/dialogs/baseDialog/BaseDialog";
import BaseSelect from "../../../components/baseSelect/BaseSelect";
import APIErrorHelper from "../../../helpers/APIErrorHelper";
import ValidationHelper from "../../../helpers/ValidationHelper";
import CamplaCoreService from "../../../services/CamplaCoreService";
import PluginHelper from "../../../helpers/PluginHelper";

class ISAcademiaImport extends React.Component {
  constructor(props) {
    super(props);
    this.fileReader = new FileReader();

    this.semesterOptions = [
      { value: "spring", label: "ModuleClasses.SemesterSpring" },
      { value: "fall", label: "ModuleClasses.SemesterFall" }
    ];
    const semesterMap = new Map();
    this.semesterOptions.forEach(s => semesterMap.set(s.value, s));

    this.validationDefinition = {
      name: {
        defaultValue: "",
        rule: ValidationHelper.isString
      },
      members: {
        defaultValue: [],
        rule: ValidationHelper.isMemberArray
      },
      moduleId: {
        defaultValue: "",
        rule: ValidationHelper.isID
      },
      isFall: {
        defaultValue: true,
        rule: ValidationHelper.isBoolean
      },
      year: {
        defaultValue: parseInt(new Date().getFullYear()),
        initValid: true,
        rule: ValidationHelper.isYear
      }
    };
    this.state = {
      show: false,
      isLoading: false,
      extendedModuleClass: null,
      modulesMap: new Map(),
      semesterMap: semesterMap,
      moduleOptions: [],
      importSuccessful: false,
      validation: ValidationHelper.getValidation(this.validationDefinition),
      dialogApiError: ""
    };

    this.onSubmit = this.onSubmit.bind(this);
    this.onHide = this.onHide.bind(this);
    this.onUpload = this.onUpload.bind(this);
    this.fetchData = this.fetchData.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.translateOptions = this.translateOptions.bind(this);
    this.resolveModuleById = this.resolveModuleById.bind(this);
    this.extractData = this.extractData.bind(this);
    this.validateStudents = this.validateStudents.bind(this);
  }

  handleChange(event) {
    this.setState({
      validation: ValidationHelper.update(event, this.state.validation)
    });
  }

  translateOptions(t, options) {
    if (Array.isArray(options)) {
      return options.map(o => ({ value: o.value, label: t(o.label) }));
    } else {
      return { value: options.value, label: t(options.label) };
    }
  }

  onHide() {
    this.setState({
      extendedModuleClass: null,
      importSuccessful: false,
      isLoading: false,
      show: false
    });
  }

  async onSubmit() {
    if (this.state.validation._isValid) {
      const validatedData = ValidationHelper.getData(this.state.validation);
      const userId = CamplaCoreService.getAuthenticatedUser()._id;

      const setupClasses = await PluginHelper.calculateAndCreateClasses(validatedData, userId, () => {
        console.error("Creation of class failed");
        this.setState({
          dialogApiError: APIErrorHelper.getError(
              {status: 500},
              () => this.setState({ dialogApiError: "" }),
              {},
              this.props.t
          )
        });
      })
      if (setupClasses){
        this.props.onCreate({
          class: setupClasses.createdClasses,
          moduleClass: setupClasses.createdModuleClasses
        });
        this.setState({
          extendedModuleClass: null,
          importSuccessful: false,
          isLoading: false,
          show: false,
          validation: ValidationHelper.getValidation(this.validationDefinition)
        });
      }
    }
  }

  resolveModuleById(id) {
    const m = this.state.modulesMap.has(id)
      ? this.state.modulesMap.get(id)
      : this.state.modulesMap.values().next().value;
    return m ? { value: m._id, label: m.name } : null;
  }

  async fetchData() {
    try {
      const modules = await CamplaCoreService.getAllModules();
      const moduleOptions = modules.map(m => ({ value: m._id, label: m.name }));
      const modulesMap = new Map();
      modules.forEach(m => modulesMap.set(m._id, m));
      this.setState({ modulesMap: modulesMap, moduleOptions });
      this.handleChange({
        target: {
          name: "moduleId",
          value: modules[0]._id
        }
      });
    } catch (err) {
      this.setState({
        apiError: APIErrorHelper.getError(
          err,
          () => this.setState({ apiError: "" }),
          {},
          this.props.t
        )
      });
    }
  }

  onUpload(event) {
    this.fileReader.readAsBinaryString(event.target.files[0]);
  }

  async validateStudents(workbook){
    if (
        workbook["SheetNames"].length !== 1
    ) {
      throw new Error("Invalid xlsx document given");
    }

    const sheet = workbook["SheetNames"][0];
    const students = XLSX.utils.sheet_to_json(
        workbook.Sheets[sheet],
        {
          header: ["surname", "name", "Geburtsdatum", "Matrikel-Nr", "Kurzz.", "Studiengang", "Kommentar", "Vertiefung", "Status", "Modultyp", "Lehrform", "Durchführung", "Mobile", "mail"],
          range: 13
        }
    );

    if (!Array.isArray(students) || students.length === 0) {
      throw new Error("Invalid xlsx document given");
    }

    const studentProfiles = students
        .filter(
            rawSt =>
                typeof rawSt["mail"] !== "undefined" && rawSt["mail"].indexOf("@") > 0
        )
        .map(rawSt => ({
          email: rawSt["mail"],
          firstname: rawSt["name"],
          lastname: rawSt["surname"]
        }));

    let s = -1;
    const buckets = [];
    const validatedStudents = [];
    let studentCount = 0;
    for (let i = 0; i < studentProfiles.length; i++) {
      if (i % 5 === 0) {
        s++;
        buckets[s] = [];
      }
      buckets[s].push(studentProfiles[i]);
      studentCount++;
    }
    buckets.forEach(profiles => profiles.forEach(p => validatedStudents.push(p)))
    return validatedStudents;
  }

  async extractData(workbook) {
    const students = await PluginHelper.validateStudentsEmails((await this.validateStudents(workbook)));
    const now = new Date();
    return {
      members: students,
      moduleId: null,
      className: "",
      isFall: now.getMonth() > 7 || now.getMonth() < 1,
      year: now.getFullYear()
    };
  }

  componentDidMount() {
    this.fetchData();
    this.fileReader.onload = async evt => {
      this.setState({ isLoading: true });
      try {
        const importedData = await this.extractData(
          XLSX.read(evt.target.result, { type: "binary" })
        );
        const validation = {
          name: {
            defaultValue: importedData.className,
            initValid: true,
            rule: ValidationHelper.isString
          },
          members: {
            defaultValue: importedData.members,
            initValid: true,
            rule: ValidationHelper.isMemberArray
          },
          moduleId: {
            defaultValue: importedData.moduleId ? importedData.moduleId : "",
            initValid: false,
            rule: ValidationHelper.isID
          },
          isFall: {
            defaultValue: importedData.isFall,
            initValid: true,
            rule: ValidationHelper.isBoolean
          },
          year: {
            defaultValue: importedData.year,
            initValid: true,
            rule: ValidationHelper.isYear
          }
        };
        this.setState({
          validation: ValidationHelper.getValidation(validation),
          importSuccessful: true
        });
        this.handleChange({
          target: {
            name: "year",
            value: importedData.year
          }
        });
      } catch (err) {
        const error = APIErrorHelper.getError(
          { status: 412 },
          () => this.setState({ dialogApiError: "" }),
          {},
          this.props.t
        );
        this.setState({ dialogApiError: error });
      }
      this.setState({ isLoading: false });
    };
  }

  render() {
    const { t } = this.props;
    // eslint-disable-next-line
    const upload =
      !this.state.importSuccessful && !this.state.isLoading ? (
        <Form>
          <p>
            {typeof window.plugins["isacademia"] !== "undefined"
                && typeof window.plugins["isacademia"]["link"] !== "undefined"
                && window.plugins.isacademia.link.length > 0 ? (
                <button
                    href={typeof window.plugins.isacademia !== "undefined" ? window.plugins.isacademia.link : ""}
                    className="btn btn-green"
                    target="_blank" rel="noreferrer"
                >
                  {t("Plugins.ISAcademiaImport.Form.ISAcademiaLink")}
                </button>
            ) : ""}
            <br />
            <b>{t("Plugins.ISAcademiaImport.Form.Description")}</b> <br />
            <b>{t("Plugins.ISAcademiaImport.Form.GuidelineTextStepFinal")}</b>
          </p>
          <Form.File id="custom-file" label="Custom file input" custom>
            <Form.File.Input
              onChange={this.onUpload}
              accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            />
            <Form.File.Label
              data-browse={t("Plugins.ISAcademiaImport.Form.UploadButton")}
            />
          </Form.File>
        </Form>
      ) : (
        ""
      );
    const loading = this.state.isLoading ? (
      <div>
        <FontAwesomeIcon icon={faCircleNotch} spin />{" "}
        {t("Plugins.ISAcademiaImport.Loading")}
      </div>
    ) : (
      ""
    );
    const form = this.state.importSuccessful ? (
      <Accordion defaultActiveKey="0">
        <Card>
          <Card.Header className="pt-0 pb-0 pl-2 pr-2 custom-card-header">
            <Accordion.Toggle
              as={Button}
              variant="link"
              className="acc-link"
              eventKey="0"
            >
              <FontAwesomeIcon
                icon={faExclamationCircle}
                className={
                  this.state.validation.name.isValid &&
                    this.state.validation.members.isValid
                    ? "display-none"
                    : "mr-2 color-red"
                }
              />
              <span>{t("Plugins.ISAcademiaImport.Form.ToggleClass")}</span>
            </Accordion.Toggle>
          </Card.Header>
          <Accordion.Collapse eventKey="0">
            <Card.Body>
              <Form.Group controlId="formClassName">
                <Form.Label>
                  {t("Plugins.ISAcademiaImport.Form.ClassName")}
                </Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  placeholder={t(
                    "Plugins.ISAcademiaImport.Form.ClassNamePlaceholder"
                  )}
                  value={this.state.validation.name.value}
                  isInvalid={!this.state.validation.name.isValid}
                  onChange={this.handleChange}
                />
                <Form.Control.Feedback type="invalid">
                  {t("Plugins.ISAcademiaImport.Form.ValidationError.Name")}
                </Form.Control.Feedback>
              </Form.Group>
              <span>
                {t("Plugins.ISAcademiaImport.Form.Students")}
                <Badge variant="secondary" className="ml-1">
                  {this.state.validation.members.value.length}
                </Badge>
              </span>
              <Table size="sm" className="students-table mt-2">
                <thead>
                  <tr>
                    <th>{t("Plugins.ISAcademiaImport.Form.ColumnName")}</th>
                    <th>{t("Plugins.ISAcademiaImport.Form.ColumnMail")}</th>
                  </tr>
                </thead>
                <tbody>
                  {this.state.validation.members.value.map(m => (
                    <tr>
                      <td>{m.name}</td>
                      <td>{m.email}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Accordion.Collapse>
        </Card>
        <Card>
          <Card.Header className="pt-0 pb-0 pl-2 pr-2 custom-card-header">
            <Accordion.Toggle
              as={Button}
              variant="link"
              className="acc-link"
              eventKey="1"
            >
              <FontAwesomeIcon
                icon={faExclamationCircle}
                className={
                  this.state.validation.moduleId.isValid &&
                    this.state.validation.year.isValid &&
                    this.state.validation.isFall.isValid
                    ? "display-none"
                    : "mr-2 color-red"
                }
              />
              <span>{t("Plugins.ISAcademiaImport.Form.ToggleModuleClass")}</span>
            </Accordion.Toggle>
          </Card.Header>
          <Accordion.Collapse eventKey="1">
            <Card.Body>
              <Form.Group controlId="formBasicModules">
                <Form.Label>
                  {t("Plugins.ISAcademiaImport.Form.Module")}
                </Form.Label>
                <BaseSelect
                  options={this.state.moduleOptions}
                  placeholder={t(
                    "Plugins.ISAcademiaImport.Form.ModulePlaceholder"
                  )}
                  onChange={s =>
                    this.handleChange({
                      target: {
                        name: "moduleId",
                        value: s.value
                      }
                    })
                  }
                  value={this.resolveModuleById(
                    this.state.validation.moduleId.value
                  )}
                />
                <Form.Control
                  type="hidden"
                  isInvalid={!this.state.validation.moduleId.isValid}
                />
                <Form.Control.Feedback type="invalid">
                  {t("Plugins.ISAcademiaImport.Form.ValidationError.ModuleId")}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group controlId="formBasicYear">
                <Form.Label>{t("Plugins.ISAcademiaImport.Form.Year")}</Form.Label>
                <FormControl
                  type="number"
                  min={new Date().getFullYear() - 5}
                  max={new Date().getFullYear() + 5}
                  value={this.state.validation.year.value}
                  isInvalid={!this.state.validation.year.isValid}
                  onChange={event =>
                    this.handleChange({
                      target: {
                        name: "year",
                        value: parseInt(event.target.value)
                      }
                    })
                  }
                  onBlur={event => {
                    const val = parseInt(event.target.value);
                    const currentYear = new Date().getFullYear();
                    this.handleChange({
                      target: {
                        name: "year",
                        value:
                          val > currentYear - 5 && val < currentYear + 5
                            ? val
                            : currentYear
                      }
                    });
                  }}
                />
                <Form.Control.Feedback type="invalid">
                  {t("Plugins.ISAcademiaImport.Form.ValidationError.Year")}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group controlId="formBasicSemester">
                <Form.Label>
                  {t("Plugins.ISAcademiaImport.Form.Semester")}
                </Form.Label>
                <BaseSelect
                  isSearchable={false}
                  options={this.translateOptions(t, this.semesterOptions)}
                  onChange={selected => {
                    this.handleChange({
                      target: {
                        name: "isFall",
                        value: selected.value === "fall"
                      }
                    });
                  }}
                  placeholder={t(
                    "Plugins.ISAcademiaImport.Form.SemesterPlaceholder"
                  )}
                  value={this.translateOptions(
                    t,
                    this.state.semesterMap.get(
                      this.state.validation.isFall.value ? "fall" : "spring"
                    )
                  )}
                />
                <Form.Control
                  type="hidden"
                  isInvalid={!this.state.validation.isFall.isValid}
                />
                <Form.Control.Feedback type="invalid">
                  {t("Plugins.ISAcademiaImport.Form.ValidationError.IsFall")}
                </Form.Control.Feedback>
              </Form.Group>
            </Card.Body>
          </Accordion.Collapse>
        </Card>
      </Accordion>
    ) : (
      ""
    );
    return (
      <>
        <Dropdown.Item onClick={() => this.setState({ show: true })}>
          {t("Plugins.ISAcademiaImport.Button")}
        </Dropdown.Item>
        <BaseDialog
          show={this.state.show}
          onHide={this.onHide}
          dialogApiError={this.state.dialogApiError}
          title={t("Plugins.ISAcademiaImport.Title")}
          leftButtonCallback={this.onHide}
          leftButtonText={t("DialogCancel")}
          leftButtonVariant="black"
          rightButtonCallback={this.onSubmit}
          rightButtonDisabled={!this.state.validation._isValid}
          rightButtonText={t("Plugins.ISAcademiaImport.Form.Submit")}
          rightButtonVariant="green"
          body={(<>
            {upload}
            {loading}
            {form}
          </>)}
        />
      </>
    );
  }
}

export default withTranslation()(ISAcademiaImport);
